import React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";

import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import { useNavigate } from "react-router-dom";
import themestyle from "../theme";
import { useQuery } from "@apollo/client/react";
import { ME, GET_INSTRUCTOR_DASHBOARD_DATA } from "../graphql/query";
import { useMemo } from "react";
import CenteredLoader from "./CenteredLoader";

type Day = { label: string; date: string; active?: boolean };

const getDynamicWeek = (): Day[] => {
  const today = new Date();
  const currentDay = today.getDay(); 
  const distanceToMonday = currentDay === 0 ? -6 : 1 - currentDay; 
  const monday = new Date(today);
  monday.setDate(today.getDate() + distanceToMonday);
  const week: Day[] = [];
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    week.push({
      label: days[d.getDay()],
      date: d.getDate().toString(),
      active: d.toDateString() === today.toDateString(),
    });
  }
  return week;
};

const WEEK: Day[] = getDynamicWeek();



function statusChip(status: "Completed" | "Ongoing" | "Upcoming" | "Cancelled") {
  if (status === "Completed") {
    return (
      <Chip
        size="small"
        label="Completed"
        sx={{ height: 20, fontWeight: 900, bgcolor: "#EEF2F7", color: "#64748B", fontSize: 11 }}
      />
    );
  }
  if (status === "Ongoing") {
    return (
      <Chip
        size="small"
        label="Ongoing"
        sx={{ height: 20, fontWeight: 900, bgcolor: "#DFF7EA", color: "#a3d65c", fontSize: 11 }}
      />
    );
  }
  if (status === "Cancelled") {
    return (
      <Chip
        size="small"
        label="Cancelled"
        sx={{ height: 20, fontWeight: 900, bgcolor: "#FEF2F2", color: "#B91C1C", fontSize: 11 }}
      />
    );
  }
  return (
    <Chip
      size="small"
      label="Upcoming"
      sx={{ height: 20, fontWeight: 900, bgcolor: "#E8F1FF", color: "#2563EB", fontSize: 11 }}
    />
  );
}

export default function InstructorDashboard() {
  const navigate = useNavigate();
  const today = new Date();
  const localToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const futureDate = new Date(today);
  futureDate.setDate(today.getDate() + 90);
  const localFutureDate = `${futureDate.getFullYear()}-${String(futureDate.getMonth() + 1).padStart(2, "0")}-${String(futureDate.getDate()).padStart(2, "0")}`;

  const { data: dashboardData, loading: dashboardLoading } = useQuery(GET_INSTRUCTOR_DASHBOARD_DATA, {
    variables: {
      startDate: localToday,
      endDate: localFutureDate,
    },
    fetchPolicy: "network-only",
  });

  const { data: meData }: any = useQuery(ME, {
    fetchPolicy: "network-only",
  });

  const instructorUpdates: any[] = dashboardData?.getInstructorDashboardData?.updates?.updates ?? [];
  const classesData = dashboardData?.getInstructorDashboardData?.classes ?? [];
  const instructorInfo = dashboardData?.getInstructorDashboardData?.instructor;

  const relativeTime = (isoStr: string): string => {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
  };

  const updateIcon = (type: string) => {
    if (type === "enrollment") return <Avatar sx={{ width: 34, height: 34, bgcolor: "#DFF7EA", color: "#22c55e", fontWeight: 900, fontSize: 14 }}>S</Avatar>;
    if (type === "attendance") return <Avatar sx={{ width: 34, height: 34, bgcolor: "#F0F0F0", color: "#64748B" }}><FactCheckRoundedIcon sx={{ fontSize: 18 }} /></Avatar>;
    if (type === "notification") return <Avatar sx={{ width: 34, height: 34, bgcolor: "#EEF2FF", color: "#6366F1" }}><MailOutlineRoundedIcon sx={{ fontSize: 18 }} /></Avatar>;
    return <Avatar sx={{ width: 34, height: 34, bgcolor: "#F9EFD7", color: "#f59e0b" }}><CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} /></Avatar>;
  };

  let storedUser: any = null;
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    storedUser = null;
  }
  const displayName = meData?.me?.fullname || instructorInfo?.fullname || storedUser?.fullname || "Instructor";
  const getOrdinalSuffix = (d: number) => {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const dayOfMonth = today.getDate();
  const weekday = today.toLocaleDateString("en-US", { weekday: "long" });
  const month = today.toLocaleDateString("en-US", { month: "long" });
  const formattedDate = `${weekday}, ${month} ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)}`;

  const currentHour = today.getHours();
  let greetingTime = "Good evening";
  if (currentHour < 12) {
    greetingTime = "Good morning";
  } else if (currentHour < 18) {
    greetingTime = "Good afternoon";
  }

  const todayClasses = useMemo(() => {
    if (!classesData || classesData.length === 0) return [];
    const upcomingEvents: any[] = [];
    classesData.forEach((c: any) => {
        let dates =
          Array.isArray(c.scheduledDates) && c.scheduledDates.length > 0
            ? c.scheduledDates
            : c.day
            ? [c.day]
            : [];
            
        dates.forEach((dateStr: string) => {
            if (dateStr >= localToday) {
                upcomingEvents.push({ ...c, specificDate: dateStr });
            }
        });
    });

    return upcomingEvents
      .map((c: any) => {
        const [startH, startM] = (c.startTime || "00:00").split(":").map(Number);
        const [endH, endM] = (c.endTime || "00:00").split(":").map(Number);
        const [y, m, d] = c.specificDate.split("-").map(Number);
        const start = new Date(y, m - 1, d, startH, startM, 0);
        const end = new Date(y, m - 1, d, endH, endM, 0);
        let status: "Completed" | "Ongoing" | "Upcoming" | "Cancelled" = "Upcoming";
        const isCancelled = c.isCancelled || (Array.isArray(c.cancelledDates) && c.cancelledDates.includes(c.specificDate));
        
        if (isCancelled) status = "Cancelled";
        else if (today > end) status = "Completed";
        else if (today >= start && today <= end) status = "Ongoing";
        const diffMs = end.getTime() - start.getTime();
        const diffMins = Math.round(diffMs / 60000);
        const formatTime = (timeStr: string) => {
          if (!timeStr) return "";
          const [h, min] = timeStr.split(":");
          const hh = parseInt(h);
          const ampm = hh >= 12 ? "PM" : "AM";
          const h12 = hh % 12 || 12;
          return `${String(h12).padStart(2, "0")}:${min} ${ampm}`;
        };

        let dateLabel = "";
        if (c.specificDate === localToday) {
            dateLabel = "Today";
        } else {
            const dateObj = new Date(y, m - 1, d);
            dateLabel = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        }
        return {
          id: `${c.id}_${c.specificDate}`,
          time: formatTime(c.startTime),
          duration: `${diffMins} mins`,
          status,
          title: c.className,
          location: c.classType ? c.classType.replace(/_/g, " ") : "Studio",
          accent: status === "Ongoing" ? "success" : "muted",
          dateLabel,
          _rawStart: start,
        };
      })
      .sort((a, b) => {
        const statusOrder = { Ongoing: 1, Upcoming: 2, Completed: 3, Cancelled: 4 };
        if (statusOrder[a.status] !== statusOrder[b.status]) {
          return statusOrder[a.status] - statusOrder[b.status];
        }
        return a._rawStart.getTime() - b._rawStart.getTime();
      })
      .slice(0, 3);
  }, [classesData, localToday, today]);

  if (dashboardLoading) {
    return <CenteredLoader />;
  }

  return (
    <Stack spacing={2.25} sx={{ px: { xs: 1, sm: 0 } }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, color: themestyle.colors.primary, pt: 2 }}>
          Instructor Dashboard
        </Typography>
        <Typography sx={{ mt: 0.5, color: themestyle.colors.textSecondary, fontWeight: 700, fontSize: 13 }}>
          {greetingTime}, {displayName}. Here is your schedule for{" "}
          <Box component="span" sx={{ color: "#a3d65c", fontWeight: 900 }}>
            {formattedDate}
          </Box>
          .
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: 2,
          "& > *": {
            minWidth: 0,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Card elevation={0} sx={{ border: `1px solid ${themestyle.colors.bordercolor}`, borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, "&:last-child": { pb: { xs: 1.75, sm: 2.25 } } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarMonthOutlinedIcon sx={{ color: "#a3d65c" }} />
                  <Typography sx={{ fontWeight: 600, color: themestyle.colors.primary }}>
                    Weekly Schedule
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: { xs: 0.75, sm: 2 },
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 800 }}>
                    {today.toLocaleDateString("en-US", { month: "long" })} {today.getFullYear()}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() => navigate("/calendar")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 900,
                      color: "#a3d65c",
                      px: 0,
                      "&:hover": { bgcolor: "transparent" },
                    }}
                  >
                    For more details
                  </Button>
                </Box>
              </Stack>

              <Box
                sx={{
                  mt: 2,
                  overflowX: { xs: "auto", sm: "visible" },
                  pl: { xs: 0.5, sm: 0 },
                  pr: { xs: 1.5, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 1.25,
                    minWidth: { xs: 440, sm: "auto" },
                  }}
                >
                  {WEEK.map((d) => (
                    <Box
                      key={`${d.label}-${d.date}`}
                      sx={{
                        borderRadius: 2,
                        border: d.active ? `1.5px solid #a3d65c` : "1.5px solid transparent",
                        bgcolor: d.active ? "#f0f9e4" : "transparent",
                        py: 1.25,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 900, display: "block" }}>
                        {d.label}
                      </Typography>
                      <Typography sx={{ color: themestyle.colors.primary, fontWeight: 900, lineHeight: 1.1 }}>
                        {d.date}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Typography sx={{ mt: 2.25, mb: 1.25, fontWeight: 900, color: themestyle.colors.primary }}>
            Classes
          </Typography>
          <Stack spacing={1.5}>
            {todayClasses.length === 0 ? (
              <Typography sx={{ color: "#94A3B8", fontWeight: 600, fontSize: 13, py: 2 }}>No upcoming classes scheduled.</Typography>
            ) : todayClasses.map((c: any) => {
                const isOngoing = c.status === "Ongoing";
              return (
                <Card
                  key={c.title}
                  elevation={0}
                  sx={{
                    border: `1px solid ${themestyle.colors.bordercolor}`,
                    borderRadius: 2,
                    boxShadow: isOngoing ? "0 8px 20px rgba(16,24,40,0.08)" : "none",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {isOngoing ? (
                    <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, bgcolor: "#a3d65c" }} />
                  ) : null}
                  {isOngoing ? (
                    <Box
                      sx={{
                        position: "absolute",
                        right: 10,
                        top: 10,
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        bgcolor: "#a3d65c",
                      }}
                    />
                  ) : null}
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                      <Box sx={{ width: { xs: "100%", sm: 140 }, flexShrink: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: isOngoing ? "#a3d65c" : "#64748B", fontSize: 13 }}>
                          {c.time}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.2 }} flexWrap="nowrap" alignItems="center">
                          <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 800, whiteSpace: "nowrap" }}>
                            {c.dateLabel}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 800 }}>
                            &bull;
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 800, whiteSpace: "nowrap" }}>
                            {c.duration}
                          </Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {statusChip(c.status)}
                        <Typography sx={{ mt: 0.5, fontWeight: 900, color: themestyle.colors.primary }} noWrap>
                          {c.title}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                          <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 800 }} noWrap>
                            {c.location}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>

        <Card
          elevation={0}
          sx={{
            border: `1px solid ${themestyle.colors.bordercolor}`,
            borderRadius: 2,
            height: "fit-content",
            mb: { xs: 2, sm: 0 },
            minWidth: 0,
            width: "100%",
          }}
        >
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1}
              sx={{ p: { xs: 1.75, sm: 2.25 } }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <NotificationsOutlinedIcon sx={{ color: "#a3d65c" }} />
                <Typography sx={{ fontWeight: 900, color: themestyle.colors.primary }}>
                  Updates
                </Typography>
              </Stack>

            </Stack>
            <Divider />

            <Box 
              sx={{ 
                maxHeight: 550, 
                overflowY: "auto",
                px: { xs: 1.75, sm: 2.25 },
                pb: { xs: 1.75, sm: 2.25 },
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  bgcolor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "#E5E7EB",
                  borderRadius: "10px",
                },
              }}
            >
              <List dense sx={{ py: 1.25 }}>
                {instructorUpdates.length === 0 ? (
                  <Typography sx={{ color: "#94A3B8", fontWeight: 600, fontSize: 13, py: 2 }}>
                    No recent updates.
                  </Typography>
                ) : (
                  instructorUpdates.map((u: any) => (
                    <ListItem
                      key={u.id}
                      disableGutters
                      alignItems="flex-start"
                      sx={{
                        py: 1.25,
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 44, mt: 0.5 }}>{updateIcon(u.type)}</ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: 12.75, fontWeight: 800, color: themestyle.colors.primary, lineHeight: 1.25 }}>
                            {u.title}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block", lineHeight: 1.4 }}>
                              {u.message}
                            </Typography>
                            <Typography variant="caption" sx={{ color: themestyle.colors.textSecondary, fontWeight: 800 }}>
                              {relativeTime(u.time)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
